/* eslint-disable @next/next/no-img-element */
import { Box, Button, Grid } from "@mui/material";
import {
  ProductDetailsStatusEnum,
  formatCurrency,
  promoPriceToSRP,
  colors,
} from "common";
import BazarAvatar from "components/BazarAvatar";
import BazarButton from "components/BazarButton";
import { CounterButtons } from "components/buttons";
import LazyImage from "components/LazyImage";
import { H1, H2, H6, Tiny } from "components/Typography";
import { CartItem } from "modules/cart/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ImageViewer from "react-simple-image-viewer";
import { FlexBox, FlexRowCenter } from "../flex-box";
import { ProductDetailsType } from "modules/product-details/types";
import Countdown from "pages-sections/fashion-shop/countdown";
import _ from "lodash";
import { usePcwUserCart } from "hooks/use-pcw-cart";
import { useLoginStatus } from "hooks/useLoginStatus";
import { useDialogueShow } from "hooks/useDialogue";
import { useLinkHandler } from "hooks";
import { useIsMobile } from "hooks";
import { AvailableBranchesData } from "modules/products/types";
import { startCase } from "lodash";
import { promotionalsFlag } from "common/constants/feature-toggles";
import { PromotionalContentModal } from "components/promotional/PromotionalContentModal";
import { useRouter } from "next/router";
import { MessengerFB } from "components/icons/MessengerFB";
import { useBranchSelection } from "hooks/use-branch-selection";

const branchPointerMsg = "(Your selected branch)";

// ================================================================
type ProductIntroProps = {
  product: ProductDetailsType;
  productImagesProp: string[];
  hasStocks?: boolean;
  stocksLeft?: number;
  availableBranches?: AvailableBranchesData;
  isLoading?: boolean;
};
// ================================================================

const ProductIntro: React.FC<ProductIntroProps> = ({
  product,
  productImagesProp,
  hasStocks,
  stocksLeft,
  availableBranches,
  isLoading,
}) => {
  const {
    userCartLoading,
    addUserCartItem,
    userCartProductQtyInCartViaSlug,
    userCartItemStocks,
  } = usePcwUserCart();
  const { isUserLogin } = useLoginStatus();
  const { showLoginFormOpen } = useDialogueShow();
  //TODO: only use for event
  const { openFbMessengerWithMessage } = useLinkHandler();
  const isMobile = useIsMobile();
  const { query } = useRouter();
  const { selectedBranchId } = useBranchSelection();

  const isLogin = isUserLogin;

  const [selectedImage, setSelectedImage] = useState(0);
  const [toBuyQuantity, setToBuyQuantity] = useState<number>(1);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [promotionalID, setPromotionalID] = useState<string>("");
  const [showContent, setShowContent] = useState<boolean>(false);

  const isNewProduct = useMemo(
    () => !!product.is_latest_product,
    [product.is_latest_product]
  );

  const productImages = useMemo(
    () =>
      productImagesProp?.filter(
        (value, index, array) => array.indexOf(value) === index
      ),
    [productImagesProp]
  );

  const handleImageClick = (ind: number) => () => {
    setSelectedImage(ind);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const onClickAddCart = useCallback(() => {
    const { product_slug } = product;
    if (!product_slug) {
      return;
    }

    setToBuyQuantity(1);

    if (isLogin) {
      addUserCartItem(product_slug, toBuyQuantity, selectedBranchId);
    } else {
      showLoginFormOpen();
    }
  }, [
    product,
    toBuyQuantity,
    isLogin,
    addUserCartItem,
    selectedBranchId,
    showLoginFormOpen,
  ]);

  const nonBundleAddAmount = useMemo(() => {
    let additionalFee = 0;
    if (product?.category === "CPU" && !product?.with_bundle) {
      additionalFee = product?.non_bundle_addtl_amount || 0;
    }
    return `${formatCurrency(additionalFee)}`;
  }, [product]);

  const cpuMoboBundleCopy = useMemo(() => {
    if (product?.category === "CPU") {
      if (product?.with_bundle) {
        return `MUST BE PURCHASED WITH COMPATIBLE MOTHERBOARD`;
      }
      if (product?.non_bundle_addtl_amount) {
        return `ADDITIONAL FEE OF ${nonBundleAddAmount} APPLIES WHEN BUYING THIS CPU WITHOUT COMPATIBLE MOTHERBOARD`;
      }
    }
    return "";
  }, [nonBundleAddAmount, product]);

  const inquireProductMessage = useMemo(() => {
    return `Hi, I would like to inquire about the product "${product?.product_name}". Thank you!
                                                                                                                                                                                                                      
    Product Link: ${window.location.href}`;
  }, [product]);

  const qtyOfThisProductInCart = useMemo(() => {
    return userCartProductQtyInCartViaSlug(product?.product_slug);
  }, [product?.product_slug, userCartProductQtyInCartViaSlug]);

  const userCartStocksLeft = useMemo(() => {
    return userCartItemStocks(product?.product_slug);
  }, [product?.product_slug, userCartItemStocks]);

  const addToCartDisabled = useMemo(() => {
    if (hasStocks === false) {
      return true;
    }
    if (qtyOfThisProductInCart === 0) {
      return false;
    }
    if (
      qtyOfThisProductInCart >= userCartStocksLeft ||
      qtyOfThisProductInCart <= 0
    ) {
      return true;
    }
    return false;
  }, [hasStocks, qtyOfThisProductInCart, userCartStocksLeft]);

  const openContent = (val: string) => {
    setShowContent(true);
    setPromotionalID(val);
  };

  useEffect(() => {
    setSelectedImage(0);
  }, [productImages]);

  useEffect(() => {
    // Reset to buy quantity when query changes
    setToBuyQuantity(1);
  }, [query, stocksLeft]);

  return (
    <Box width="100%">
      <Grid container spacing={3} justifyContent="space-around">
        <Grid item md={6} xs={12} alignItems="center">
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
            }}
          >
            {!isMobile && (
              <Box>
                {productImages?.length > 1 ? (
                  <FlexBox
                    overflow={"hidden"}
                    sx={{
                      flexDirection: "column",
                      maxHeight: "380px",
                      overflowY: "auto",
                      direction: "rtl",
                      "&::-webkit-scrollbar": {
                        width: "0px", // Hide scrollbar by default
                      },
                      "&:hover::-webkit-scrollbar": {
                        width: "3px", // Show scrollbar on hover
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,0.3)", // Customize scrollbar thumb if needed
                        borderRadius: "4px",
                      },
                      mr: 1,
                    }}
                  >
                    {/* TODO: copy this */}
                    {productImages?.map((item, ind) => (
                      <FlexRowCenter
                        key={ind}
                        width={64}
                        height={64}
                        minWidth={64}
                        bgcolor="white"
                        border="1px solid"
                        borderRadius="10px"
                        ml={ind === 0 ? "auto" : 0}
                        style={{
                          cursor: "pointer",
                          margin: 3,
                        }}
                        onClick={handleImageClick(ind)}
                        mr={ind === productImages?.length - 1 ? "auto" : "10px"}
                        borderColor={
                          selectedImage === ind ? "primary.main" : "grey.400"
                        }
                      >
                        <BazarAvatar
                          src={item}
                          variant="square"
                          height={60}
                          width={60}
                        />
                      </FlexRowCenter>
                    ))}
                  </FlexBox>
                ) : null}
              </Box>
            )}
            <FlexBox justifyContent="center">
              <LazyImage
                width={isMobile ? 300 : 650}
                height={isMobile ? 300 : 600}
                alt={`${product?.product_name}_img`}
                loading="eager"
                objectFit="contain"
                src={
                  productImages[selectedImage] ||
                  "/assets/images/kachi/kachi-crying.png"
                }
                onClick={() => openImageViewer(selectedImage)}
              />

              {isViewerOpen && (
                <ImageViewer
                  closeOnClickOutside
                  disableScroll
                  src={productImages}
                  onClose={closeImageViewer}
                  currentIndex={currentImage}
                  backgroundStyle={{
                    backgroundColor: "rgba(0,0,0,0.9)",
                    zIndex: 1501,
                  }}
                />
              )}
            </FlexBox>
          </Box>

          {isMobile && (
            <>
              {productImages?.length > 1 ? (
                <FlexBox overflow="auto">
                  {productImages?.map((item, ind) => (
                    <FlexRowCenter
                      key={ind}
                      width={64}
                      height={64}
                      minWidth={64}
                      bgcolor="white"
                      border="1px solid"
                      borderRadius="10px"
                      mt={2}
                      ml={ind === 0 ? "auto" : 0}
                      style={{ cursor: "pointer" }}
                      onClick={handleImageClick(ind)}
                      mr={ind === productImages?.length - 1 ? "auto" : "10px"}
                      borderColor={
                        selectedImage === ind ? "primary.main" : "grey.400"
                      }
                    >
                      <BazarAvatar src={item} variant="square" height={40} />
                    </FlexRowCenter>
                  ))}
                </FlexBox>
              ) : null}
            </>
          )}
        </Grid>

        <Grid item md={6} xs={12} alignItems="center">
          {isNewProduct && (
            <LazyImage
              src={"/assets/images/badges/new-badge.png"}
              width={"50px"}
              height={"24px"}
            />
          )}
          <Box mb={2} mt={0.5}>
            <H1>{product?.ec_display_name || product?.product_name}</H1>
            {product?.sku && <Tiny>{product?.sku}</Tiny>}
          </Box>

          {/* Contact Us Button */}
          <Button
            color="info"
            variant="outlined"
            sx={{
              color: "#0c314b",
              width: "auto",
              mb: 2.25,
              height: 40,
              "@media (max-width: 850px)": {
                height: "auto",
                fontSize: 11.5,
              },
              borderColor: "#0c314b",
            }}
            startIcon={
              <MessengerFB
                fill="#0c314b"
                sx={{
                  fontSize: { xs: 15, sm: 22 },
                  color: "#7d879c",
                  opacity: 1,
                }}
              />
            }
            onClick={() => openFbMessengerWithMessage(inquireProductMessage)}
          >
            Message Us
          </Button>

          {promotionalsFlag && product?.promotional_badges?.length > 0 && (
            <>
              <FlexBox
                sx={{
                  mb: "16px",
                  justifyContent: "start",
                  columnGap: ".5em",
                }}
              >
                {product.promotional_badges.map((i) => (
                  <img
                    key={i.slug}
                    src={
                      i?.badge_url || "/assets/images/kachi/kachi-crying.png"
                    }
                    alt={i.slug}
                    width={undefined}
                    height={isMobile ? "30em" : "40em"}
                    style={{ cursor: "pointer" }}
                    onClick={() => openContent(i.id)}
                  />
                ))}
              </FlexBox>
              <PromotionalContentModal
                uuid={promotionalID}
                open={showContent}
                onClose={() => setShowContent(false)}
              />
            </>
          )}
          {product?.mfr_logo ? (
            <FlexBox alignItems="center" mb={2}>
              <LazyImage
                width={50}
                height={30}
                layout="fixed"
                objectFit="contain"
                src={product?.mfr_logo}
              />
            </FlexBox>
          ) : null}
          {/* TODO: Maybe later on add ratings? */}
          {/* <FlexBox alignItems="center" mb={2}>
            <Box lineHeight="1">Rated:</Box>
            <Box mx={1} lineHeight="1">
              <BazarRating color="warn" fontSize="1.25rem" value={5} readOnly />
            </Box>
            <H6 lineHeight="1">(50)</H6>
          </FlexBox> */}
          <Box mb={3}>
            <H2 color="primary.main" mb={0.5} lineHeight="1">
              {formatCurrency(product?.retail_price)}
            </H2>
            <H6
              color="grey.600"
              mb={0.5}
              lineHeight="1"
              sx={{ textDecoration: "line-through" }}
            >
              {formatCurrency(promoPriceToSRP(product?.retail_price))}
            </H6>
            <Tiny fontWeight="600" color={colors.error}>
              {cpuMoboBundleCopy}
            </Tiny>

            {hasStocks || availableBranches?.available_to?.length ? (
              <Box mt={2}>
                {/* Available to branches. */}
                {availableBranches?.available_to?.length > 0 ? (
                  <H6 color={colors.green[700]} fontWeight={700}>
                    Available to:
                  </H6>
                ) : null}
                {availableBranches?.available_to?.map((item) => (
                  <li key={item?.id}>{startCase(item?.branch_name)}</li>
                ))}

                {/* Not available to branches. */}
                {availableBranches?.no_stock_to?.length > 0 ? (
                  <H6 color="#2B3445" fontWeight={700} sx={{ mt: 1 }}>
                    Currently Not Available to:
                  </H6>
                ) : null}
                {availableBranches?.no_stock_to?.map((item) => (
                  <li
                    key={item?.id}
                    style={{
                      color: `${
                        item?.id === selectedBranchId ? colors.secondary : ""
                      }`,
                    }}
                  >{`${startCase(item?.branch_name)} ${
                    item?.id === selectedBranchId ? branchPointerMsg : ""
                  }`}</li>
                ))}
              </Box>
            ) : (
              <H6
                sx={{
                  color: "orange",
                }}
              >
                {isLoading ? " " : "Out of stock"}
              </H6>
            )}
          </Box>
          {product?.flash_deal ? (
            <Box my={5}>
              {product?.stocks_left <= 2 ? (
                <H6 color={colors.error} sx={{ fontWeight: "700" }}>
                  {product?.stocks_left} STOCKS LEFT !!!
                </H6>
              ) : null}
              <Box sx={{ maxWidth: 300 }}>
                <Countdown expireDate={new Date(product?.deal_end)} />
              </Box>
            </Box>
          ) : null}
          <Box>
            <CounterButtons
              min={1}
              value={toBuyQuantity}
              max={
                qtyOfThisProductInCart > 0
                  ? userCartStocksLeft - qtyOfThisProductInCart
                  : stocksLeft // seperate stocks left data
              }
              onChangeValue={(num) => setToBuyQuantity(num)}
              disabled={
                //TODO: this line of code since ISR implemented it is not good for dynamic branch selectors.
                // product?.status?.code !== ProductDetailsStatusEnum.available ||
                addToCartDisabled
              }
            />
          </Box>
          <BazarButton
            color="primary"
            variant="contained"
            disabled={addToCartDisabled || userCartLoading}
            onClick={onClickAddCart}
            sx={{ mb: 4.5, px: "1.75rem", height: 40 }}
          >
            Add to Cart
          </BazarButton>
          {qtyOfThisProductInCart ? (
            <Box mt={-4}>
              <Tiny>{qtyOfThisProductInCart} pc(s) already in cart</Tiny>
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductIntro;
